import { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import { useQueryClient } from "@tanstack/vue-query";
import type { ComputedRef, InjectionKey, Ref } from "vue";
import { computed, ref, watch } from "vue";
import { useToast } from "vue-toast-notification";
import type {
  SubmissionReadOverride,
  TodosSubmissionsOverride,
} from "@/components/MyStudents/Submissions/components/SubmissionsInformation/types";
import { ERROR_TIMEOUT } from "@/constants.js";
import profileService from "@/services/profile.js";
import { hasTiptapText } from "@/util/tiptap";
import { TODO_SUBMISSION_QUERY_KEY_PREFIX } from "./useSubmissionReviewData";
import { useUpdateSubmissionFeedback } from "./useUpdateSubmissionFeedback";

export interface UseTodoSubmissionStatusArgs {
  todoSubmission: Ref<TodosSubmissionsOverride | null | undefined>;
  recentSubmission: Ref<SubmissionReadOverride | null | undefined>;
  applicationId: Ref<string>;
  todoId: Ref<string>;
}

export interface TodoSubmissionStatusApi {
  displayedStatus: Ref<TodosSubmissionsStatus | undefined>;
  permittedStatuses: ComputedRef<TodosSubmissionsStatus[]>;
  isTodoArchived: ComputedRef<boolean>;
  hasSubmission: ComputedRef<boolean>;
  isUpdatingStatus: Ref<boolean>;
  pendingStatusChange: Ref<TodosSubmissionsStatus | undefined>;
  requiredFeedbackMode: Ref<boolean>;
  selectStatus: (derived: TodosSubmissionsStatus) => Promise<boolean>;
  confirm: (feedback?: object) => Promise<boolean>;
  revert: () => void;
}

export const TODO_SUBMISSION_STATUS_KEY: InjectionKey<TodoSubmissionStatusApi> = Symbol("todoSubmissionStatus");

export const useTodoSubmissionStatus = ({
  todoSubmission,
  recentSubmission,
  applicationId,
  todoId,
}: UseTodoSubmissionStatusArgs): TodoSubmissionStatusApi => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateSubmissionFeedback } = useUpdateSubmissionFeedback();

  const displayedStatus = ref<TodosSubmissionsStatus | undefined>(todoSubmission.value?.status);
  const lastConfirmedStatus = ref<TodosSubmissionsStatus | undefined>(todoSubmission.value?.status);
  const pendingStatusChange = ref<TodosSubmissionsStatus | undefined>(undefined);
  const requiredFeedbackMode = ref<boolean>(false);
  const isUpdatingStatus = ref<boolean>(false);

  // Resync with server truth whenever the todoSubmission query refetches.
  watch(
    () => todoSubmission.value?.status,
    (status) => {
      displayedStatus.value = status;
      lastConfirmedStatus.value = status;
    },
  );

  const permittedStatuses = computed<TodosSubmissionsStatus[]>(() => todoSubmission.value?.permitted_statuses ?? []);

  const isTodoArchived = computed<boolean>(() => !!todoSubmission.value?.archived_at);

  const hasSubmission = computed<boolean>(() => !!recentSubmission.value);

  const persistStatus = async (derived: TodosSubmissionsStatus, feedback?: object): Promise<boolean> => {
    const submission = recentSubmission.value;
    let succeeded = false;
    isUpdatingStatus.value = true;
    try {
      const overrideStatus = derived === TodosSubmissionsStatus.NotSubmitted ? null : derived;
      await profileService.upsertTodoStatusOverride(applicationId.value, todoId.value, overrideStatus);
      if (feedback && submission) {
        // The feedback mutation invalidates the todoSubmission query on success.
        await updateSubmissionFeedback({
          submissionId: submission.id,
          status: submission.status,
          feedback,
        });
      } else {
        await queryClient.invalidateQueries({ queryKey: [TODO_SUBMISSION_QUERY_KEY_PREFIX] });
      }
      lastConfirmedStatus.value = derived;
      succeeded = true;
    } catch {
      displayedStatus.value = lastConfirmedStatus.value;
      toast.open({
        message: "Error while updating Submission. Please, try it later.",
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
      await queryClient.invalidateQueries({ queryKey: [TODO_SUBMISSION_QUERY_KEY_PREFIX] });
    } finally {
      isUpdatingStatus.value = false;
    }
    return succeeded;
  };

  const selectStatus = async (derived: TodosSubmissionsStatus): Promise<boolean> => {
    if (derived === displayedStatus.value) {
      return false;
    }
    displayedStatus.value = derived;
    if (
      hasSubmission.value &&
      derived === TodosSubmissionsStatus.RequiresAChange &&
      !hasTiptapText(recentSubmission.value?.feedback)
    ) {
      pendingStatusChange.value = derived;
      requiredFeedbackMode.value = true;
      return false;
    }
    pendingStatusChange.value = undefined;
    requiredFeedbackMode.value = false;
    return await persistStatus(derived);
  };

  const confirm = async (feedback?: object): Promise<boolean> => {
    const pending = pendingStatusChange.value;
    let succeeded = false;
    if (pending) {
      succeeded = await persistStatus(pending, feedback);
      pendingStatusChange.value = undefined;
      requiredFeedbackMode.value = false;
    }
    return succeeded;
  };

  const revert = (): void => {
    displayedStatus.value = lastConfirmedStatus.value;
    pendingStatusChange.value = undefined;
    requiredFeedbackMode.value = false;
  };

  return {
    displayedStatus,
    permittedStatuses,
    isTodoArchived,
    hasSubmission,
    isUpdatingStatus,
    pendingStatusChange,
    requiredFeedbackMode,
    selectStatus,
    confirm,
    revert,
  };
};
